import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Loading, EmptyState } from "@/components/common";
import MultiSelect from "@/components/MultiSelect";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Building2, MapPin, GitCompare, X, Star, Bell, Award, BookOpen } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all";
export const inr = (n) => (n ? `₹${Number(n).toLocaleString("en-IN")}` : "—");

export function CollegeLogo({ src, size = 5 }) {
  const [bad, setBad] = useState(!src);
  return bad ? <Building2 className={size === 6 ? "w-6 h-6 text-[#312E81]" : "w-5 h-5 text-[#312E81]"} /> : <img src={src} alt="" className="w-full h-full object-contain" onError={() => setBad(true)} />;
}

export function SingleSelect({ value, onChange, options, placeholder, testid }) {
  return (
    <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
      <SelectTrigger className="w-40 bg-white" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="bg-white max-h-72"><SelectItem value={ALL}>{placeholder}</SelectItem>{(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export const toQuery = (f) => new URLSearchParams(Object.entries(f).reduce((a, [k, v]) => { const s = Array.isArray(v) ? v.join("|") : v; if (s) a[k] = s; return a; }, {}));

export function CompareDialog({ ids, open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (open && ids.length) api.get(`/finder/compare?ids=${ids.join(",")}`).then(({ data }) => setRows(data)); }, [open, ids]);
  const lines = [["Location", (c) => `${c.city}, ${c.state}`], ["Category", (c) => c.category], ["Established", (c) => c.established_year || "—"],
    ["Accreditation", (c) => c.accreditation || "—"], ["Courses", (c) => c.course_count], ["1st-year fee range", (c) => `${inr(c.min_fee)} – ${inr(c.max_fee)}`], ["Website", (c) => c.website || "—"]];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-5xl max-h-[85vh] overflow-auto">
        <DialogHeader><DialogTitle>Compare Colleges</DialogTitle></DialogHeader>
        <table className="w-full text-sm" data-testid="compare-table">
          <thead><tr><th className="text-left p-2 w-32"></th>{rows.map((c) => <th key={c.id} className="text-left p-2 font-heading text-[#312E81]">{c.name}</th>)}</tr></thead>
          <tbody>{lines.map(([l, fn]) => <tr key={l} className="border-t border-slate-100"><td className="p-2 text-xs text-slate-500">{l}</td>{rows.map((c) => <td key={c.id} className="p-2 text-slate-700 break-words">{fn(c)}</td>)}</tr>)}</tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}

const SEAT_CHIP = { Available: "bg-emerald-500 text-white", Closing: "bg-amber-500 text-white", "Under Waiting": "bg-sky-500 text-white", Closed: "bg-rose-500 text-white" };
function SeatChips({ s }) {
  const e = Object.entries(s || {}).filter(([, n]) => n);
  if (!e.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1" data-testid="college-card-seats">
      {Object.keys(SEAT_CHIP).filter((k) => s[k]).map((k) => <span key={k} className={`px-2 py-0.5 rounded-full text-[10px] font-bold flex items-center gap-1 ${SEAT_CHIP[k]}`}><span className={`w-1.5 h-1.5 rounded-full bg-white/80 ${k === "Available" ? "animate-pulse" : ""}`} />{s[k]} {k}</span>)}
    </div>
  );
}

function CollegeCard({ c, checked, onCompare, onOpen }) {
  const alerts = (c.admission_alerts || []).length;
  return (
    <div onClick={onOpen} className="cursor-pointer min-w-0 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-[box-shadow,transform] flex flex-col" data-testid="college-card">
      <div className="flex items-center justify-between mb-3">
        {c.is_featured ? <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold flex items-center gap-1"><Star className="w-3 h-3" /> Featured</span> : <span />}
        <div className="flex items-center gap-2">
          {alerts > 0 && <span className="px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-600 text-[10px] font-bold flex items-center gap-0.5" title="Admission alerts"><Bell className="w-3 h-3" />{alerts}</span>}
          <label onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCompare(); }} className="text-[10px] flex items-center gap-1 text-slate-500 cursor-pointer" data-testid={`compare-check-${c.id}`}>
            <input type="checkbox" readOnly checked={checked} className="accent-[#4F46E5]" /> Compare
          </label>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <div className="w-14 h-14 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0 overflow-hidden"><CollegeLogo src={c.logo} /></div>
        <div className="min-w-0">
          <h3 className="font-heading font-bold text-[#312E81] leading-tight break-words">{c.name}</h3>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.city}, {c.state}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
        <div className="bg-slate-50 rounded-lg p-2"><div className="text-[10px] uppercase tracking-wider text-slate-400">Category</div><div className="font-medium text-slate-700 line-clamp-2">{c.category || "—"}</div></div>
        <div className="bg-slate-50 rounded-lg p-2"><div className="text-[10px] uppercase tracking-wider text-slate-400">Established</div><div className="font-medium text-slate-700">{c.established_year || "—"}</div></div>
      </div>
      {c.accreditation && <div className="mt-2 text-[11px] text-emerald-700 flex items-center gap-1"><Award className="w-3 h-3" /> {c.accreditation}</div>}
      <SeatChips s={c.seat_summary} />
      <div className="flex items-center justify-between mt-auto pt-4">
        <span className="text-xs text-slate-600 flex items-center gap-1"><BookOpen className="w-3.5 h-3.5 text-[#4F46E5]" /> {c.course_count || 0} Courses</span>
        <Button size="sm" className="bg-[#4F46E5] hover:bg-[#4338CA] h-8" data-testid={`college-view-${c.id}`}>View</Button>
      </div>
    </div>
  );
}

const EMPTY = { search: "", address: "", states: [], cities: [], categories: [], courses: [], level: "", fee_range: "" };

export default function Colleges() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [opts, setOpts] = useState({});
  const [f, setF] = useState(EMPTY);
  const [cmp, setCmp] = useState([]);
  const [cmpOpen, setCmpOpen] = useState(false);
  const load = (q = f) => { setRows(null); api.get(`/finder/colleges?${toQuery(q)}`).then(({ data }) => setRows(data)); };
  useEffect(() => { api.get("/finder/filters").then(({ data }) => setOpts(data)); }, []);
  useEffect(() => { load(); }, [f.states, f.cities, f.categories, f.courses, f.level, f.fee_range]); // eslint-disable-line
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const toggleCmp = (id) => setCmp((c) => (c.includes(id) ? c.filter((x) => x !== id) : c.length >= 4 ? (toast.error("Compare up to 4 colleges"), c) : [...c, id]));
  const active = JSON.stringify(f) !== JSON.stringify(EMPTY);

  return (
    <div>
      <div className="rounded-2xl bg-gradient-to-br from-[#312E81] to-[#4F46E5] text-white p-6 sm:p-8 mb-5" data-testid="colleges-header">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">Find Featured Colleges</h1>
        <p className="text-indigo-100 mt-2 max-w-2xl text-sm sm:text-base">Explore top-rated colleges across India. Filter by state, city, category, or course to find the perfect institution.</p>
        <div className="flex flex-wrap gap-2 mt-5">
          <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={f.search} onChange={(e) => set("search")(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search colleges by name…" className="pl-9 bg-white text-slate-800 h-11" data-testid="colleges-search" /></div>
          <div className="relative flex-1 min-w-[220px]"><MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={f.address} onChange={(e) => set("address")(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search by address / area…" className="pl-9 bg-white text-slate-800 h-11" data-testid="colleges-address" /></div>
          <Button onClick={() => load()} className="h-11 bg-white text-[#312E81] hover:bg-indigo-50" data-testid="colleges-search-btn">Search</Button>
          <Button disabled={cmp.length < 2} onClick={() => setCmpOpen(true)} className="h-11 bg-amber-400 text-[#1E1B4B] hover:bg-amber-300" data-testid="compare-btn"><GitCompare className="w-4 h-4 mr-1" /> Compare Colleges ({cmp.length})</Button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-4 flex flex-wrap gap-2 items-center">
        <MultiSelect label="Select States" options={opts.states} value={f.states} onChange={set("states")} testid="colleges-state" />
        <MultiSelect label="Select Cities" options={opts.cities} value={f.cities} onChange={set("cities")} testid="colleges-city" />
        <MultiSelect label="Select Categories" options={opts.categories} value={f.categories} onChange={set("categories")} testid="colleges-category" />
        <MultiSelect label="Select Courses" options={opts.courses} value={f.courses} onChange={set("courses")} testid="colleges-course" width="w-56" />
        <SingleSelect value={f.level} onChange={set("level")} options={opts.levels} placeholder="All Levels" testid="colleges-level" />
        <SingleSelect value={f.fee_range} onChange={set("fee_range")} options={opts.fee_ranges} placeholder="All Fee Ranges" testid="colleges-fee" />
        {active && <Button variant="ghost" size="sm" onClick={() => setF(EMPTY)} data-testid="colleges-clear"><X className="w-4 h-4 mr-1" /> Clear filters</Button>}
        {cmp.length > 0 && <Button variant="ghost" size="sm" onClick={() => setCmp([])} data-testid="compare-clear">Clear compare</Button>}
      </div>
      <div className="flex items-baseline gap-3 mb-3"><h2 className="font-heading font-bold text-lg text-[#312E81]">Featured Colleges</h2>{rows && <span className="text-sm text-slate-500" data-testid="colleges-count">{rows.length} colleges found</span>}</div>
      {rows === null ? <Loading label="Loading colleges" /> : rows.length === 0 ? <div className="bg-white rounded-xl border"><EmptyState icon={Building2} title="No colleges found" subtitle="Try removing a filter" /></div> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {rows.map((c) => <CollegeCard key={c.id} c={c} checked={cmp.includes(c.id)} onCompare={() => toggleCmp(c.id)} onOpen={() => window.open(`/colleges/${c.id}`, "_blank", "noopener")} />)}
        </div>
      )}
      <CompareDialog ids={cmp} open={cmpOpen} onOpenChange={setCmpOpen} />
    </div>
  );
}
